import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-control text-sm font-medium ' +
    'transition-[color,background-color,box-shadow,filter] cursor-pointer ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ' +
    'focus-visible:ring-[color:var(--kz-color-border-focus)] ' +
    'disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-[color:var(--kz-color-brand-primary)] text-white shadow-sm hover:brightness-95 active:brightness-90',
        secondary:
          'bg-[color:var(--kz-color-surface-sunken)] text-[color:var(--kz-color-ink-primary)] hover:brightness-95',
        outline:
          'border border-[color:var(--kz-color-border-default)] bg-transparent text-[color:var(--kz-color-ink-primary)] hover:bg-[color:var(--kz-color-surface-sunken)]',
        ghost:
          'bg-transparent text-[color:var(--kz-color-ink-primary)] hover:bg-[color:var(--kz-color-surface-sunken)]',
        destructive:
          'bg-[color:var(--kz-color-status-danger)] text-white hover:brightness-95',
        link: 'text-[color:var(--kz-color-brand-primary)] underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-5 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-12 rounded-lg px-7 text-base',
        icon: 'h-10 w-10',
        // Child mode — larger tap target per PROJECT_BRIEF §7
        child: 'h-14 px-7 text-base rounded-xl',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp ref={ref} className={cn(buttonVariants({ variant, size, className }))} {...props} />
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
